
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface PhoneVerificationOptions {
  onVerificationComplete?: () => void;
  verificationType?: 'sms' | 'phone_change';
}

export function usePhoneVerification(options: PhoneVerificationOptions = {}) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showVerification, setShowVerification] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { 
    onVerificationComplete = () => {},
    verificationType = 'phone_change'
  } = options;

  const initiatePhoneVerification = async (phone: string) => {
    if (!phone) return false;
    
    setLoading(true);
    
    try {
      // Validate the phone number basic format
      if (!phone.startsWith('+')) {
        throw new Error('Phone number must start with country code (e.g., +61)');
      }
      
      // For existing users, use updateUser instead of signInWithOtp
      const { error } = await supabase.auth.updateUser({
        phone
      });

      if (error) throw error;

      // Store the phone number for verification
      setPhoneNumber(phone);
      setShowVerification(true);
      
      toast.success('Verification code sent! Please check your phone for an SMS with a 6-digit code.');
      return true;
    } catch (error: any) {
      console.error("Phone verification error:", error);
      toast.error(error.message || "Failed to send verification code");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (otp: string): Promise<void> => {
    if (!phoneNumber || !otp) return;
    
    setLoading(true);
    
    try {
      console.log(`Verifying OTP: ${otp} for phone: ${phoneNumber} with type: ${verificationType}`);
      
      // Use only valid MobileOtpType values
      const { error } = await supabase.auth.verifyOtp({
        phone: phoneNumber,
        token: otp,
        type: verificationType === 'phone_change' ? 'phone_change' : 'sms'
      });

      if (error) throw error;

      toast.success('Phone number verified successfully');
      setShowVerification(false);
      onVerificationComplete();
    } catch (error: any) {
      console.error("OTP verification error:", error);
      toast.error(error.message || "Failed to verify phone number");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const cancelVerification = () => {
    setShowVerification(false);
    setPhoneNumber("");
  };

  const resendCode = async () => {
    if (!phoneNumber) return;
    
    setLoading(true);
    
    try {
      // For resending, we should use updateUser again
      const { error } = await supabase.auth.updateUser({
        phone: phoneNumber
      });
      
      if (error) throw error;
      
      toast.success('New verification code sent to your phone');
    } catch (error: any) {
      toast.error(error.message || "Failed to send new verification code");
    } finally {
      setLoading(false);
    }
  };

  return {
    phoneNumber,
    showVerification,
    loading,
    initiatePhoneVerification,
    verifyOtp,
    cancelVerification,
    resendCode
  };
}
